import React, { FC, ReactElement, useCallback } from 'react';

import { useMfaIssueDetailsQuery } from 'src/apps/mfa/hooks/useMfaIssueDetailsQuery';
import { ActionButtons } from 'src/apps/mobile-common/components/action-buttons/ActionButtons';
import { MobileTicketsSystems } from 'src/apps/mobile-common/components/mobile-tickets-systems/MobileTicketsSystems';
import { mapMobileIssueDetailsToReport } from 'src/apps/mobile-common/helpers/MobileIssueMapper';
import { useMobileSdkIssueDetailsQuery } from 'src/apps/mobile-sdk/hooks/useMobileSdkIssueDetailsQuery';
import { MobileComponentDetailsBlock } from 'src/common/components/mobile-component-details-block/MobileComponentDetailsBlock';
import { formatShortMonthDatetime } from 'src/common/helpers/DateFormatHelper';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { getBaseURL } from 'src/common/helpers/UrlNormalizingHelper';
import { ProductType } from 'src/common/interfaces/Tenant';
import { usePlatformConfigStore } from 'src/common/stores/platformConfigStore';

import { PRODUCT_NAME } from '../types/IssuesTypes';

import {
	IMobileDashboardDetailsBlockProps,
	IMobileDashboardDetailsDispatchProps,
	IMobileDashboardInnerBlockProps
} from './MobileDashboardDetailsBlock.types';

const MobileDashboardInnerBlock: FC<IMobileDashboardInnerBlockProps> = ({
	query,
	issueId,
	issuePageUrl,
	productType
}) => {
	const { isDefaultJiraLabelsEnabled } = usePlatformConfigStore();
	const urlToCopy = `${getBaseURL()}${issuePageUrl}`;

	const openIssueInNewTab = useCallback(() => {
		window.open(issuePageUrl, '_blank');
	}, [issuePageUrl]);

	const actionButtons: ReactElement = (
		<>
			<ActionButtons textToCopy={urlToCopy} openNewTab={openIssueInNewTab} />
			{!query.isLoading && query.data && (
				<MobileTicketsSystems
					issue={mapMobileIssueDetailsToReport(
						query.data,
						issueId,
						isDefaultJiraLabelsEnabled,
						productType
					)}
				/>
			)}
		</>
	);

	return (
		<MobileComponentDetailsBlock
			isLoading={query.isLoading}
			issue={query.data}
			scanName={query.data?.scanName ?? ''}
			runTime={formatShortMonthDatetime(query.data?.scanDate ?? '') ?? ''}
			issueNumber={query.data?.elementIndex ?? ''}
			actionButtons={actionButtons}
		/>
	);
};

const MfaDashboardDetailsBlock: FC<IMobileDashboardDetailsBlockProps> = ({
	runId,
	issueId,
	setError
}) => {
	const query = useMfaIssueDetailsQuery(issueId, runId, setError);
	const issuePageUrl = RoutesHelper.getMfaIssueInfoPath({ runId, issueId });

	return (
		<MobileDashboardInnerBlock
			query={query}
			issueId={issueId}
			issuePageUrl={issuePageUrl}
			productType={ProductType.MOBILE_FLOW_ANALYZER}
		/>
	);
};

const MobileSdkDashboardDetailsBlock: FC<IMobileDashboardDetailsBlockProps> = ({
	runId,
	issueId,
	setError
}) => {
	const query = useMobileSdkIssueDetailsQuery(issueId, runId, setError);
	const issuePageUrl = RoutesHelper.getMobileSdkIssueInfoPath({ testId: runId, issueId });

	return (
		<MobileDashboardInnerBlock
			query={query}
			issueId={issueId}
			issuePageUrl={issuePageUrl}
			productType={ProductType.MOBILE_SDK}
		/>
	);
};

export const MobileDashboardDetailsBlock: FC<IMobileDashboardDetailsDispatchProps> = ({
	runId,
	issueId,
	productName,
	setError
}) => {
	if (productName === PRODUCT_NAME.MOBILE_FLOW_ANALYZER) {
		return <MfaDashboardDetailsBlock runId={runId} issueId={issueId} setError={setError} />;
	}

	return <MobileSdkDashboardDetailsBlock runId={runId} issueId={issueId} setError={setError} />;
};
